import { useMutation } from '@tanstack/react-query';
import {
  ActiveSourcingChannelDetails,
  SourcingService,
} from 'api-clients/monolith';
import { FormattedMessage, useIntl } from 'react-intl';

import ArrowUpIcon from '@/assets/icons/arrow-up.svg';
import { useConfirmation } from '@/components/base/confirmation';
import { useToaster } from '@/components/base/toaster';
import { BulbIcon } from '@/components/icons/bulb';
import { CloseIcon } from '@/components/icons/close';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { useInvalidateCampaignQueries } from '@/hooks/use-invalidate-campaign-queries';

interface ChannelRecommendationProps {
  channel: ActiveSourcingChannelDetails;
}

export function ChannelRecommendation({ channel }: ChannelRecommendationProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();

  const { recommendation } = channel;
  const { notifySuccess, notifyError } = useToaster();
  const invalidateCampaignQueries = useInvalidateCampaignQueries();
  const { confirm } = useConfirmation();

  const { mutate: handleAcceptRecommendation } = useMutation({
    mutationFn: () =>
      SourcingService.patchInternalApiSourcingSourcingPurchasesAcceptRecommendation(
        channel.sourcing_purchase_id!,
      ),
    onSuccess: async () => {
      await invalidateCampaignQueries();
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Recommendation applied successfully!',
          description:
            'Toast after user accepts an automated channel budget recommendation',
        }),
      });
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to update campaign!',
          description:
            'Error toast when accepting a recommendation request fails',
        }),
      });
    },
  });
  const { mutate: handleRejectRecommendation } = useMutation({
    mutationFn: () =>
      SourcingService.patchInternalApiSourcingSourcingPurchasesRejectRecommendation(
        channel.sourcing_purchase_id!,
      ),
    onSuccess: async () => {
      await invalidateCampaignQueries();
      notifySuccess({
        message: intl.formatMessage({
          defaultMessage: 'Recommendation cleared successfully!',
          description:
            'Toast after user dismisses a channel recommendation without applying',
        }),
      });
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to clear recommendation!',
          description:
            'Error toast when dismissing a recommendation request fails',
        }),
      });
    },
  });
  if (
    !channel.sourcing_purchase_id ||
    !recommendation ||
    !recommendation.budget
  )
    return null;

  const { budget } = recommendation;
  const isBudgetIncrease = budget > 0;

  const handleAcceptBudgetClick = async () => {
    const recommendedBudget = (channel.budget_stats?.expected ?? 0) + budget;
    const confirmed = await confirm({
      title: intl.formatMessage({
        defaultMessage: 'Confirm budget change',
        description:
          'Modal title: user is about to apply a system-suggested spend change',
      }),
      description: intl.formatMessage(
        {
          defaultMessage:
            'Are you sure you want to accept this budget? This will {margin} your campaign budget to {value}.',
          description:
            'Confirm dialog body when accepting a recommended budget change for a channel',
        },
        {
          value: formatCurrency(recommendedBudget),
          margin: isBudgetIncrease ? 'increase' : 'decrease',
        },
      ),
      confirmLabel: intl.formatMessage(
        {
          defaultMessage: 'Yes, {margin} budget',
          description:
            'Primary button to confirm increasing or decreasing campaign budget',
        },
        { margin: isBudgetIncrease ? 'increase' : 'decrease' },
      ),
    });

    if (confirmed) {
      handleAcceptRecommendation();
    }
  };

  return (
    <div className="bg-fx-blue-50 mx-5 mb-5 flex items-center justify-between gap-2 rounded-sm px-2 py-1.5">
      <div className="flex items-center gap-2">
        <BulbIcon className="text-fx-blue" />

        <div className="flex flex-col">
          <Typography variant="body2">
            <FormattedMessage
              defaultMessage="{margin} budget"
              values={{ margin: isBudgetIncrease ? 'Increase' : 'Decrease' }}
              description="Confirm dialog: {margin} is Increase or Decrease for budget change"
            />
          </Typography>
          <Typography variant="subtitle1">
            {budget > 0 ? (
              <FormattedMessage
                defaultMessage="We recommend increasing this campaign budget by {value}."
                values={{ value: formatCurrency(Math.abs(budget)) }}
                description="Localized currency line for a positive recommended budget change"
              />
            ) : (
              <FormattedMessage
                defaultMessage="We recommend descreasing this campaign budget by {value}."
                values={{ value: formatCurrency(Math.abs(budget)) }}
                description="Localized currency line when the system suggests lowering spend"
              />
            )}
          </Typography>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button
          variant="secondary"
          onClick={() => void handleAcceptBudgetClick()}
        >
          {isBudgetIncrease && <img src={ArrowUpIcon} alt="Edit" />}
          <FormattedMessage
            defaultMessage="{margin} budget"
            values={{ margin: isBudgetIncrease ? 'Increase' : 'Decrease' }}
            description="CTA to accept Increase/Decrease on the in-card recommendation"
          />
        </Button>
        <Button
          variant="icon"
          size="icon"
          onClick={() => void handleRejectRecommendation()}
          className="flex h-6 w-6 items-center justify-center"
        >
          <CloseIcon className="text-fx-primary hover:text-fx-primary" />
        </Button>
      </div>
    </div>
  );
}
